// Canvas-rendered series can't consume CSS custom properties, so the chart
// data colors live here as hex, one set per theme. Keep in sync with
// .claude/CHART_COLORS.md (light) and .claude/CHART_COLORS_DARK.md (dark) and
// with the matching tokens in styles/theme.css.

interface GradientStop {
  color: string;
  offset: number;
}

const buildLinearGradient = (
  direction: { x2: number; y2: number },
  colorStops: GradientStop[]
) => ({
  type: "linear" as const,
  x: 0,
  y: 0,
  ...direction,
  colorStops,
});

type LinearGradient = ReturnType<typeof buildLinearGradient>;

interface MapStop {
  color: string;
  position: number;
}

export interface ChartPalette {
  // Dot outlines, state borders and chart guide lines — the card color, so
  // they read as gaps rather than white marks.
  card: string;
  // Horizontal bars (and inline table bars): base → tip.
  barGradientH: LinearGradient;
  // Vertical bars and progress rings: top → bottom, never fading out.
  barGradientV: LinearGradient;
  // Purple band between the impressions and spend lines.
  bandGradient: LinearGradient;
  // Display slice of the two-slice channel donut — the only orange gradient.
  displayGradient: LinearGradient;
  guideLine: string;
  // Hover fill for a map state.
  mapHover: string;
  // Sequential map scale, no-data state first.
  mapStops: MapStop[];
  orange: string;
  // Fill for the "Other" slice.
  other: string;
  // Part-to-whole order for donuts with 3+ slices (never more than 6 slices
  // in total with "Other").
  partToWhole: string[];
  // The primary data color: impressions, volume.
  primary: string;
  // Orange fill from the spend line down to the baseline.
  spendGradient: LinearGradient;
}

const ORANGE = "#f85636";
const ORANGE_60 = "#fb9a86";
const PURPLE = "#9920e7";

const SPEND_GRADIENT = buildLinearGradient({ x2: 0, y2: 1 }, [
  { color: "rgba(248, 86, 54, 0.75)", offset: 0 },
  { color: "rgba(248, 86, 54, 0.2)", offset: 1 },
]);

const DISPLAY_GRADIENT = buildLinearGradient({ x2: 0, y2: 1 }, [
  { color: ORANGE, offset: 0 },
  { color: ORANGE_60, offset: 1 },
]);

const LIGHT_PRIMARY = "#5c3f94";
const LIGHT_PURPLE_60 = "#c279f1";
const LIGHT_PURPLE_35 = "#dbb1f7";

const LIGHT_PALETTE: ChartPalette = {
  bandGradient: buildLinearGradient({ x2: 0, y2: 1 }, [
    { color: "rgba(92, 63, 148, 0.32)", offset: 0 },
    { color: "rgba(92, 63, 148, 0.16)", offset: 1 },
  ]),
  barGradientH: buildLinearGradient({ x2: 1, y2: 0 }, [
    { color: LIGHT_PRIMARY, offset: 0 },
    { color: PURPLE, offset: 1 },
  ]),
  barGradientV: buildLinearGradient({ x2: 0, y2: 1 }, [
    { color: PURPLE, offset: 0 },
    { color: LIGHT_PRIMARY, offset: 1 },
  ]),
  card: "#ffffff",
  displayGradient: DISPLAY_GRADIENT,
  guideLine: "rgba(255, 255, 255, 0.75)",
  mapHover: PURPLE,
  mapStops: [
    { color: "#f0defb", position: 0 },
    { color: PURPLE, position: 0.65 },
    { color: LIGHT_PRIMARY, position: 1 },
  ],
  orange: ORANGE,
  other: "#c4c4c4",
  partToWhole: [
    LIGHT_PRIMARY,
    LIGHT_PURPLE_60,
    ORANGE,
    ORANGE_60,
    LIGHT_PURPLE_35,
  ],
  primary: LIGHT_PRIMARY,
  spendGradient: SPEND_GRADIENT,
};

// Deep plum is 1.9:1 against the dark card, so the primary data color moves up
// to a light purple and every purple step shifts with it.
const DARK_PRIMARY = "#c279f1";
const DARK_PURPLE_35 = "#dbb1f7";
const DARK_CARD = "#232327";

const DARK_PALETTE: ChartPalette = {
  bandGradient: buildLinearGradient({ x2: 0, y2: 1 }, [
    { color: "rgba(194, 121, 241, 0.32)", offset: 0 },
    { color: "rgba(194, 121, 241, 0.16)", offset: 1 },
  ]),
  barGradientH: buildLinearGradient({ x2: 1, y2: 0 }, [
    { color: PURPLE, offset: 0 },
    { color: DARK_PRIMARY, offset: 1 },
  ]),
  barGradientV: buildLinearGradient({ x2: 0, y2: 1 }, [
    { color: DARK_PRIMARY, offset: 0 },
    { color: PURPLE, offset: 1 },
  ]),
  card: DARK_CARD,
  displayGradient: DISPLAY_GRADIENT,
  guideLine: "rgba(35, 35, 39, 0.75)",
  mapHover: DARK_PRIMARY,
  mapStops: [
    { color: "#5c3f94", position: 0 },
    { color: PURPLE, position: 0.5 },
    { color: DARK_PURPLE_35, position: 1 },
  ],
  orange: ORANGE,
  other: "#7a7a82",
  partToWhole: [DARK_PRIMARY, PURPLE, ORANGE, ORANGE_60, DARK_PURPLE_35],
  primary: DARK_PRIMARY,
  spendGradient: SPEND_GRADIENT,
};

export const getChartPalette = (isDark: boolean): ChartPalette =>
  isDark ? DARK_PALETTE : LIGHT_PALETTE;
